<template>
  <div :class="['player-actions', { 'is-mobile': isMobile }]">
    <template v-if="!isMyProfile">
      <div
        class="follow-circle-btn"
        :class="{ following: followedType === 'following', friends: followedType === 'friends' }"
        @click="handleFollowClick"
      >
        <GamIcon
          :name="
            followedType === 'follow' || followedType === 'follow-back' ? GamIconName.HEART_EMPTY : GamIconName.HEART
          "
          is-current
        />
      </div>
    </template>

    <NotificationPermissionDialog
      :is-open="askForNotificationPermission"
      :message="localizeWithValues('dialog.player.notifications.message', [player ? player.username : 'this player'])"
    />
  </div>
</template>

<script setup lang="ts">
import type { UserDto } from '@/core/data/user/user.interface';
import { gambits, localizeWithValues } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import { useAuthStore } from '@/stores/auth.store';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import NotificationPermissionDialog from '../gam-dialog/dialog-contents/NotificationPermissionDialog.vue';

interface PlayerActionProps {
  player: UserDto | null | void;
}

const props = defineProps<PlayerActionProps>();

const authStore = useAuthStore();
const { user } = storeToRefs(authStore);

const askForNotificationPermission = ref<boolean>(false);

const isMyProfile = computed(() => {
  return user.value?.id === props.player?.id;
});

const followUser = () => {
  if (!props.player) return;

  gambits.userService.toggleFollow(props.player.id, false);

  // @ts-ignore
  props.player.isFollowed = true;
  // @ts-ignore
  props.player.followers += 1;

  askForNotificationPermission.value = true;
};

const unfollowUser = () => {
  if (!props.player) return;

  gambits.userService.toggleFollow(props.player.id, true);

  // @ts-ignore
  props.player.isFollowed = false;
  // @ts-ignore
  props.player.followers -= 1;
};

const followedType = computed(() => {
  let followType = 'follow';

  // @ts-ignore
  if (props.player?.isFollowed && props.player?.isFollowingMe) {
    followType = 'friends';
    // @ts-ignore
  } else if (props.player?.isFollowed) {
    followType = 'following';
    // @ts-ignore
  } else if (props.player?.isFollowingMe) {
    followType = 'follow-back';
  }
  return followType;
});

function handleFollowClick() {
  if (followedType.value === 'friends' || followedType.value === 'following') {
    unfollowUser();
  } else {
    followUser();
  }
}
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.player-actions {
  display: flex;
  align-items: center;
}

.follow-circle-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 2rem;
  border: 1px solid var(--color-white-10);
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms linear;

  @include hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: var(--color-white-30);
  }

  &.following {
    color: var(--color-white);
    border-color: var(--color-white);
  }

  &.friends {
    color: var(--color-pink);
    border-color: var(--color-pink);
  }
}
</style>
